import type { MonthlyRevenueRecord } from '../hooks/useMonthlyRevenue';

// ─── Formatters ───────────────────────────────────────────────────────────────
export function fmtUSD(n: number): string {
  if (Math.abs(n) >= 1_000_000)
    return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)
    return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

export function fmtPct(n: number, decimals = 1): string {
  return `${n >= 0 ? '+' : ''}${n.toFixed(decimals)}%`;
}

export function fmtMonth(isoDate: string): string {
  const d = new Date(isoDate);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// ─── Year filtering ───────────────────────────────────────────────────────────
export function getAvailableYears(records: MonthlyRevenueRecord[]): number[] {
  const years = Array.from(new Set(records.map(r => r.year)));
  return years.sort((a, b) => b - a);
}

export function filterByYear(records: MonthlyRevenueRecord[], year: number | 'all'): MonthlyRevenueRecord[] {
  if (year === 'all') return records;
  return records.filter(r => r.year === year);
}

// ─── KPI computation ──────────────────────────────────────────────────────────
export interface RevenueKPIs {
  latestRecord: MonthlyRevenueRecord | null;
  prevRecord: MonthlyRevenueRecord | null;
  currentYearAvg: number;
  prevYearAvg: number;
  yoyAvgChange: number;
  maxRevenue: number;
  minRevenue: number;
  totalRevenue: number;
  recordCount: number;
}

export function computeKPIs(records: MonthlyRevenueRecord[]): RevenueKPIs {
  if (records.length === 0) {
    return {
      latestRecord: null,
      prevRecord: null,
      currentYearAvg: 0,
      prevYearAvg: 0,
      yoyAvgChange: 0,
      maxRevenue: 0,
      minRevenue: 0,
      totalRevenue: 0,
      recordCount: 0,
    };
  }

  // Records already sorted ascending
  const latestRecord = records[records.length - 1];
  const prevRecord = records.length > 1 ? records[records.length - 2] : null;

  const latestYear = latestRecord.year;
  const prevYear = latestYear - 1;

  const currentYearRecords = records.filter(r => r.year === latestYear);
  const prevYearRecords = records.filter(r => r.year === prevYear);

  const currentYearAvg =
    currentYearRecords.length > 0
      ? currentYearRecords.reduce((s, r) => s + r.global_invoiced_usd, 0) / currentYearRecords.length
      : 0;
  const prevYearAvg =
    prevYearRecords.length > 0
      ? prevYearRecords.reduce((s, r) => s + r.global_invoiced_usd, 0) / prevYearRecords.length
      : 0;

  const yoyAvgChange =
    prevYearAvg > 0 ? ((currentYearAvg - prevYearAvg) / prevYearAvg) * 100 : 0;

  const revenues = records.map(r => r.global_invoiced_usd);
  const maxRevenue = Math.max(...revenues);
  const minRevenue = Math.min(...revenues);
  const totalRevenue = revenues.reduce((s, v) => s + v, 0);

  return {
    latestRecord,
    prevRecord,
    currentYearAvg,
    prevYearAvg,
    yoyAvgChange,
    maxRevenue,
    minRevenue,
    totalRevenue,
    recordCount: records.length,
  };
}

// ─── Performance tiles ────────────────────────────────────────────────────────
export interface PerformanceTile {
  label: string;
  value: string;
  sub: string;
  accent: string; // tailwind color class for accent
  trend: 'up' | 'down' | 'neutral';
}

export function buildPerformanceTiles(kpis: RevenueKPIs, allRecords: MonthlyRevenueRecord[]): PerformanceTile[] {
  const { latestRecord, currentYearAvg, yoyAvgChange } = kpis;
  if (!latestRecord) return [];

  // Growth Rate (MoM)
  const momPct = latestRecord.mom_change_percent;
  const growthTrend = momPct >= 0 ? 'up' : 'down';
  const growthAccent = momPct >= 10 ? 'emerald' : momPct >= 0 ? 'blue' : momPct >= -20 ? 'amber' : 'red';

  // Months above avg in last 12 months
  const last12 = allRecords.slice(-12);
  const overallAvg = last12.reduce((s, r) => s + r.global_invoiced_usd, 0) / (last12.length || 1);
  const aboveAvgCount = last12.filter(r => r.global_invoiced_usd >= overallAvg).length;
  const targetPct = last12.length > 0 ? (aboveAvgCount / last12.length) * 100 : 0;

  // 3-month momentum (last 3 vs prev 3)
  const last3 = allRecords.slice(-3).reduce((s, r) => s + r.global_invoiced_usd, 0) / 3;
  const prev3 = allRecords.slice(-6, -3).reduce((s, r) => s + r.global_invoiced_usd, 0) / 3;
  const momentumPct = prev3 > 0 ? ((last3 - prev3) / prev3) * 100 : 0;

  // Volatility score from latest
  const volatilityLabel =
    latestRecord.volatility_level === 'HIGH_VOLATILITY'
      ? 'High'
      : latestRecord.volatility_level === 'MEDIUM_VOLATILITY'
      ? 'Medium'
      : 'Low';
  const volatilityAccent =
    latestRecord.volatility_level === 'HIGH_VOLATILITY'
      ? 'red'
      : latestRecord.volatility_level === 'MEDIUM_VOLATILITY'
      ? 'amber'
      : 'emerald';

  return [
    {
      label: 'Revenue Growth Rate',
      value: fmtPct(momPct),
      sub: `vs prior month`,
      accent: growthAccent,
      trend: growthTrend,
    },
    {
      label: 'Above-Average Months',
      value: `${aboveAvgCount} / ${last12.length}`,
      sub: `${targetPct.toFixed(0)}% of last 12 months`,
      accent: targetPct >= 50 ? 'emerald' : targetPct >= 33 ? 'amber' : 'red',
      trend: targetPct >= 50 ? 'up' : 'down',
    },
    {
      label: '3-Month Momentum',
      value: fmtPct(momentumPct),
      sub: `3M avg vs prior 3M avg`,
      accent: momentumPct >= 5 ? 'emerald' : momentumPct >= 0 ? 'blue' : momentumPct >= -15 ? 'amber' : 'red',
      trend: momentumPct >= 0 ? 'up' : 'down',
    },
    {
      label: 'Revenue Volatility',
      value: volatilityLabel,
      sub: `Current period assessment`,
      accent: volatilityAccent,
      trend: 'neutral',
    },
  ];
}

// ─── Trend dot color ──────────────────────────────────────────────────────────
export function trendDotColor(pos: string): string {
  if (pos === 'ABOVE_TREND' || pos === 'Above_Trend') return '#10B981'; // emerald
  if (pos === 'BELOW_TREND' || pos === 'Below_Trend') return '#EF4444'; // red
  return '#3B82F6'; // blue (Normal / AT_TREND)
}

// ─── MoM bar color ────────────────────────────────────────────────────────────
export function momBarColor(pct: number): string {
  if (Math.abs(pct) > 200) return '#F59E0B'; // extreme
  if (pct >= 20) return '#10B981';
  if (pct >= 0) return '#3B82F6';
  if (pct >= -20) return '#F97316';
  return '#EF4444';
}
